/* eslint-disable no-use-before-define */

import { AxiosError, AxiosResponse } from 'axios';
//
import {
  CLEAR_DEAL_IN_RESULTS,
  CLEAR_DEAL_EVALUATION,
  ECostFormulaParamType,
  EForecastEvaluationType,
  EEvaluationUnit,
  GET_DEAL_EVALUATION,
  GET_DEAL_EVALUATION_FAIL,
  GET_DEAL_EVALUATION_SUCCESS,
  ISummaryDeal,
  SET_COST_FORMULA_TYPE,
  SET_DEAL_IN_RESULTS,
  SET_FORECAST_TYPE,
  SET_IS_STALE,
  SET_EVALUATION_UNIT,
  SET_CURRENT_EVALUATION_UNIT,
} from 'store/deal-evaluation/types';
import { IDeal } from 'store/deals/types';
import apiRoutes from 'services/api/routes';
import apiClient from 'services/api';
import { getAccessToken } from 'lib/auth';
import { setLoading } from 'store/loading/actions';
import { ELoadingType } from 'store/loading/types';
import { getAxiosErrorMessage, handleError } from 'lib/helpers/error-handler';
import { toApi } from 'lib/helpers/deal-formatters';
import { EStorageField } from 'lib/storage';
import history from 'lib/history';
import routes from 'router/routes';
import { evaluateDeals } from 'store/deal-comparison/actions';
import { evaluateParetoDeals } from 'store/pareto/actions';
import { evaluatePortfolioDeals } from 'store/portfolio/actions';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { IStoreState } from 'store';

export function clearDealEvaluation() {
  return {
    type: CLEAR_DEAL_EVALUATION,
  };
}

export function setDealInResults(deal: IDeal) {
  return {
    type: SET_DEAL_IN_RESULTS,
    deal,
  };
}

export function clearDealInResults() {
  return {
    type: CLEAR_DEAL_IN_RESULTS,
  };
}

export function setIsStale(isStale: boolean) {
  return {
    type: SET_IS_STALE,
    isStale,
  };
}

export function setForecastType(selectedForecastType: EForecastEvaluationType) {
  return (dispatch: ThunkDispatch<IStoreState, void, AnyAction>, getState: () => IStoreState) => {
    // Update in local storage
    localStorage.setItem(EStorageField.FORECAST, selectedForecastType);

    dispatch({
      type: SET_FORECAST_TYPE,
      selectedForecastType,
    });

    // Always keep pareto and deal comparison in sync with selection
    // in case user switches to these pages after toggling.
    dispatch(evaluateDeals());
    dispatch(evaluateParetoDeals());
    dispatch(evaluatePortfolioDeals());
    if (
      history.location.pathname !== routes.compare &&
      history.location.pathname !== routes.pareto &&
      history.location.pathname !== routes.portfolio
    ) {
      dispatch(getDealEvaluation());
    }
  };
}

export function setCostFormulaType(selectedCostFormulaType: ECostFormulaParamType) {
  return (dispatch: ThunkDispatch<IStoreState, void, AnyAction>) => {
    // Update in local storage
    localStorage.setItem(EStorageField.COST_FORMULA, selectedCostFormulaType);

    dispatch({
      type: SET_COST_FORMULA_TYPE,
      selectedCostFormulaType,
    });

    // Always keep pareto and deal comparison in sync with selection
    dispatch(evaluateDeals());
    dispatch(evaluateParetoDeals());

    // Check scenario
    // 1 - Updating cost formula on deal detail page
    // 2 - Updating cost formula on deal compare page
    // 3 - Updating cost formula on pareto page
    if (history.location.pathname !== routes.compare && history.location.pathname !== routes.pareto) {
      // API call to update data based on new cost formula
      dispatch(getDealEvaluation());
    }
  };
}

export function setUnitType(selectedUnitType: EEvaluationUnit) {
  return (dispatch: ThunkDispatch<IStoreState, void, AnyAction>, getState: () => IStoreState) => {
    // Update in local storage
    localStorage.setItem(EStorageField.EVALUATION_UNIT, selectedUnitType);

    dispatch({
      type: SET_EVALUATION_UNIT,
      selectedUnitType,
    });

    // Always keep pareto and deal comparison in sync with selection
    dispatch(evaluateDeals());
    dispatch(evaluateParetoDeals());

    if (history.location.pathname !== routes.compare && history.location.pathname !== routes.pareto) {
      dispatch(getDealEvaluation());
    }
  };
}

// Takes a deal object and forecast type and resolves with an evaluation payload
// or rejects with error message
function dealEvaluationPromise(
  deal: IDeal,
  forecastType: EForecastEvaluationType,
  unitType: EEvaluationUnit,
  costFormulaType: ECostFormulaParamType
): Promise<ISummaryDeal> {
  return new Promise((resolve, reject) => {
    apiClient
      .post(apiRoutes.dealEvaluation, toApi(deal), {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
        params: {
          forecast: forecastType,
          outputUnit: unitType,
          costType: costFormulaType,
        },
      })
      .then(({ data }: AxiosResponse) => {
        resolve(data);
      })
      .catch((error: AxiosError) => {
        const errorMessage = getAxiosErrorMessage(error);
        reject(errorMessage);
      });
  });
}

export function getDealEvaluation() {
  return async (dispatch: ThunkDispatch<void, void, AnyAction>, getState: () => IStoreState) => {
    dispatch({
      type: GET_DEAL_EVALUATION,
    });
    dispatch(setLoading(ELoadingType.SPINNER));
    const { dealEdit: deal, dealEvaluation } = getState();
    const { selectedUnitType, selectedCostFormulaType } = dealEvaluation;

    try {
      // Fire evaluation calls in parallel
      const evaluations: ISummaryDeal[] = await Promise.all([
        dealEvaluationPromise(deal, EForecastEvaluationType.IHS, selectedUnitType, selectedCostFormulaType),
        dealEvaluationPromise(deal, EForecastEvaluationType.SHELL, selectedUnitType, selectedCostFormulaType),
      ]);

      // Update evaluations, deal, and mark as no longer stale
      dispatch(setIsStale(false));
      dispatch(setDealInResults(deal));
      dispatch({
        type: GET_DEAL_EVALUATION_SUCCESS,
        [EForecastEvaluationType.IHS]: evaluations[0],
        [EForecastEvaluationType.SHELL]: evaluations[1],
      });
    } catch (error) {
      handleError(error);
      dispatch({
        type: GET_DEAL_EVALUATION_FAIL,
        error,
      });
    }
    dispatch(setLoading(ELoadingType.NONE));
  };
}

export function setCurrentUnitType(selectedCurrentUnit: string) {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    dispatch({
      type: SET_CURRENT_EVALUATION_UNIT,
      selectedCurrentUnit,
    });
  };
}
